import React from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { staticPostsBySlug } from '../../data/staticBlogPosts';
import AppDownloadCTA from '../../components/AppDownloadCTA/AppDownloadCTA';
import RouteHead from '../../components/RouteHead/RouteHead';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import './BlogPostPage.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// Site-relative links in a post body stay inside the app instead of reloading the page.
const markdownComponents = {
  a: ({ href = '', children, node, ...rest }) =>
    href.startsWith('/') ? (
      <Link to={href} {...rest}>
        {children}
      </Link>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    ),
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = staticPostsBySlug[slug];

  if (!post) {
    return (
      <div className="blogPostContainer">
        <Navbar />
        <div className="blogPostError">Post not found</div>
      </div>
    );
  }

  return (
    <div className="blogPostContainer">
      <RouteHead path={`/blog/${slug}`} />

      <Navbar />

      {post.cover_image && (
        <div className="blogPostCover">
          <img src={post.cover_image} alt={post.title} />
          {post.cover_credit && <span className="blogPostCoverCredit">{post.cover_credit}</span>}
        </div>
      )}

      <article className="blogPostArticle">
        <div className="blogPostMeta">
          {post.category && <span className="blogPostCategory">{post.category}</span>}
          {post.published_at && <span className="blogPostDate">{formatDate(post.published_at)}</span>}
          {post.read_time && <span className="blogPostReadTime">{post.read_time} min read</span>}
        </div>

        <h1 className="blogPostTitle">{post.title}</h1>

        {post.excerpt && <p className="blogPostExcerpt">{post.excerpt}</p>}

        <div className="blogPostBody">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {post.body}
          </ReactMarkdown>
        </div>

        {/* CTA */}
        <AppDownloadCTA
          title={post.cta_title || 'Start Your Travel Journey with VOIA'}
          subtitle={post.cta_subtitle || 'Track your visited countries, plan trips, and connect with travelers worldwide.'}
        />

        {/* Related posts */}
        {post.related_slugs && post.related_slugs.length > 0 && (
          <div className="blogPostRelated">
            <h3>Related Posts</h3>
            <div className="blogPostRelatedLinks">
              {post.related_slugs.map((s) => (
                <Link key={s} to={`/blog/${s}`} className="blogPostRelatedLink">
                  {staticPostsBySlug[s]?.title || s.replace(/-/g, ' ')}
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <Footer variant="lightgray" />
    </div>
  );
};

export default BlogPostPage;
